/**
 * Vello shadow tokens (S055; IMPORT-0001 §3/§4). The `xs…sheet` scale is verbatim from `tokens/shadow.css`.
 *
 * Shadows are cold and rare — at rest a card uses a border, not a shadow (IMPORT-0001 §4). Elevation is
 * reserved for genuinely floating surfaces (sheets, popovers). Dark theme deepens the alpha.
 *
 * `button` and `modal` are the two hand-tuned *effect* shadows the design applies beyond the elevation scale
 * (captured verbatim from the Claude Design screens): `button` is the primary CTA's hover lift INSIDE a dialog,
 * `modal` is a focal dialog's deep elevation. They live here — the sanctioned home for raw values — so app code
 * references `--shadow-button`/`--shadow-modal` and never inlines the rgba.
 *
 * D547 (design fidelity): the web-console shadows are the A08 file's, in the navy hue `rgba(35, 48, 72, …)` — not the
 * graphite `rgba(11, 15, 26, …)` of the older Admin Web board. `cta` is the toolbar primary's hover lift (0 6px 16px .25),
 * `button` the dialog primary's (0 8px 20px .28), `thumb` the segmented thumb, `kpi` the KPI card's hover, `toggle` the
 * sidebar edge toggle, `rail-tile` the active rail tile. `card-hover` is FX-01's interactive-card lift (graphite .10, the
 * design-system effect layer). Every step stays a single `x y blur colour` layer (the native renderer parses it).
 *
 * The web-shell standard (Admin Web A08 shell 1a, sept/2026) adds four more floating surfaces, verbatim from
 * that page: `pill` (the active navigation item lifted off the navy sidebar), `menu` (a header/column popover),
 * `drawer` (the right-hand record panel) and `toast` (the bottom-centre notification).
 */
export const LIGHT_SHADOWS = {
    xs: '0 1px 2px rgba(11, 15, 26, 0.06)',
    sm: '0 2px 8px rgba(11, 15, 26, 0.08)',
    md: '0 8px 24px rgba(11, 15, 26, 0.12)',
    lg: '0 16px 48px rgba(11, 15, 26, 0.18)',
    sheet: '0 -8px 32px rgba(11, 15, 26, 0.10)',
    button: '0 8px 20px rgba(35, 48, 72, 0.28)',
    modal: '0 40px 90px rgba(35, 48, 72, 0.40)',
    pill: '0 2px 6px rgba(0, 0, 0, 0.18)',
    menu: '0 16px 40px rgba(35, 48, 72, 0.16)',
    drawer: '-24px 0 60px rgba(35, 48, 72, 0.22)',
    toast: '0 20px 50px rgba(35, 48, 72, 0.35)',
    cta: '0 6px 16px rgba(35, 48, 72, 0.25)',
    thumb: '0 1px 3px rgba(35, 48, 72, 0.12)',
    kpi: '0 8px 24px rgba(35, 48, 72, 0.08)',
    toggle: '0 2px 6px rgba(35, 48, 72, 0.14)',
    'rail-tile': '0 2px 8px rgba(0, 0, 0, 0.25)',
    'card-hover': '0 6px 18px rgba(11, 15, 26, 0.10)',
};
export const DARK_SHADOWS = {
    xs: '0 1px 2px rgba(0, 0, 0, 0.4)',
    sm: '0 2px 8px rgba(0, 0, 0, 0.45)',
    md: '0 8px 24px rgba(0, 0, 0, 0.5)',
    lg: '0 16px 48px rgba(0, 0, 0, 0.6)',
    sheet: '0 -8px 32px rgba(0, 0, 0, 0.5)',
    button: '0 8px 20px rgba(0, 0, 0, 0.55)',
    modal: '0 40px 90px rgba(0, 0, 0, 0.65)',
    pill: '0 2px 6px rgba(0, 0, 0, 0.45)',
    menu: '0 16px 40px rgba(0, 0, 0, 0.55)',
    drawer: '-24px 0 60px rgba(0, 0, 0, 0.6)',
    toast: '0 20px 50px rgba(0, 0, 0, 0.6)',
    cta: '0 6px 16px rgba(0, 0, 0, 0.5)',
    thumb: '0 1px 3px rgba(0, 0, 0, 0.4)',
    kpi: '0 8px 24px rgba(0, 0, 0, 0.45)',
    toggle: '0 2px 6px rgba(0, 0, 0, 0.45)',
    'rail-tile': '0 2px 8px rgba(0, 0, 0, 0.5)',
    'card-hover': '0 6px 18px rgba(0, 0, 0, 0.5)',
};
/**
 * The blur behind each modal layer's scrim, in px (A08: drawer 4, confirm/edit 6, palette 6, photo 8). Emitted as
 * `--scrim-blur-<layer>`; the scrim colours are the `scrim.*` colour roles.
 */
export const SCRIM_BLUR = {
    drawer: 4,
    modal: 6,
    palette: 6,
    photo: 8,
};
//# sourceMappingURL=shadow.js.map